import { routeMetaMap } from '@/config/routeMeta';
import { BreadcrumbItem } from '@/types/breadcrumb';
import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * 动态生成面包屑的 Hook
 * 根据 routeMetaMap 中的 parent 字段自动构建面包屑层级
 * 支持通过 URL 参数 `_bc_parent` 动态指定当前页面的父级路由
 *
 * @example
 * // 从病例库进入患者详情，面包屑显示：首页 > 病例库 > 患者详情
 * navigate('/patient/detail?_bc_parent=/case-history/index');
 *
 * // 从我的患者进入患者详情，面包屑显示：首页 > 我的患者 > 患者详情
 * navigate('/patient/detail?_bc_parent=/patient/list');
 */
export const useBreadcrumb = (): BreadcrumbItem[] => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath: string | undefined = location.pathname;
    const parentFromUrl = searchParams.get('_bc_parent');
    let isFirstRoute = true;

    while (currentPath) {
      const meta: { name: string; parent?: string; back?: boolean } | undefined =
        routeMetaMap[currentPath];
      if (meta) {
        breadcrumbs.unshift({
          title: meta.name,
          path: currentPath,
          back: meta.back,
        });

        // 如果是当前页面且有 _bc_parent 参数，优先使用它作为父级
        if (isFirstRoute && parentFromUrl && routeMetaMap[parentFromUrl]) {
          currentPath = parentFromUrl;
        } else {
          currentPath = meta.parent;
        }
        isFirstRoute = false;
      } else {
        break;
      }
    }

    return breadcrumbs;
  }, [location.pathname, searchParams]);
};

export default useBreadcrumb;
